import React from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    position: 'relative'
  },
  avatarWrapper: {
    position: 'relative',
    marginBottom: theme.spacing(2)
  },
  avatar: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    border: `4px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[3],
    fontSize: '3rem',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    transition: 'all 0.3s ease'
  },
  avatarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      opacity: 1
    }
  },
  uploadButton: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  deleteButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    }
  },
  helperText: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    fontSize: '0.875rem'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1
  },
  hiddenInput: {
    display: 'none'
  }
}));

// Helper function to get user initials from full name
const getUserInitials = (fullName) => {
  if (!fullName || !fullName.trim()) return '?';
  const names = fullName.trim().split(' ').filter(Boolean);
  if (names.length >= 2) {
    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  }
  return fullName[0].toUpperCase();
};

const ProfilePhotoUpload = ({
  photoUrl,
  userName,
  onUpload,
  onDelete,
  isProcessing,
  fileId
}) => {
  const classes = useStyles();
  const initials = getUserInitials(userName || '');
  const hasPhoto = photoUrl && photoUrl.trim();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onUpload(file);
    }
    // Reset input value to allow re-uploading the same file
    event.target.value = '';
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.avatarWrapper}>
        <Avatar
          src={hasPhoto ? photoUrl : ''}
          className={classes.avatar}
          alt={userName}>
          {!hasPhoto && initials}
        </Avatar>

        {isProcessing && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={40} style={{ color: 'white' }} />
          </Box>
        )}

        {!isProcessing && hasPhoto && (
          <Box className={classes.avatarOverlay}>
            <Typography
              variant="body2"
              style={{ color: 'white', fontWeight: 600 }}>
              Change Photo
            </Typography>
          </Box>
        )}

        {/* Upload Button */}
        <input
          accept="image/*"
          className={classes.hiddenInput}
          id="profile-photo-upload"
          type="file"
          onChange={handleFileChange}
          disabled={isProcessing}
        />
        <label htmlFor="profile-photo-upload">
          <Tooltip title={hasPhoto ? 'Change photo' : 'Upload photo'}>
            <IconButton
              component="span"
              className={classes.uploadButton}
              size="small"
              disabled={isProcessing}>
              <PhotoCameraIcon />
            </IconButton>
          </Tooltip>
        </label>

        {/* Delete Button - Only show if photo exists */}
        {hasPhoto && !isProcessing && fileId && (
          <Tooltip title="Delete photo">
            <IconButton
              className={classes.deleteButton}
              size="small"
              onClick={onDelete}
              disabled={isProcessing}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography className={classes.helperText}>
        {hasPhoto
          ? 'Click the camera icon to change your photo'
          : 'Upload a profile photo (JPG, PNG, or GIF)'}
      </Typography>
      <Typography className={classes.helperText} variant="caption">
        Recommended: Square image, at least 400x400 pixels
      </Typography>
    </Box>
  );
};

ProfilePhotoUpload.propTypes = {
  photoUrl: PropTypes.string,
  userName: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
  fileId: PropTypes.string
};

ProfilePhotoUpload.defaultProps = {
  photoUrl: '',
  userName: '',
  isProcessing: false,
  fileId: null
};

export default ProfilePhotoUpload;
